import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "lucide-react"

import { cn } from "../../lib/utils"

export const DialogContext = React.createContext({
   isInsideDialog: false,
})

const DialogProvider = ({ children }) => (
   <DialogContext.Provider value={{ isInsideDialog: true }}>
      {children}
   </DialogContext.Provider>
)

const Dialog = (props) => (
   <DialogProvider>
      <DialogPrimitive.Root {...props} />
   </DialogProvider>
)

function DialogTrigger({ ...props }) {
   return (
      <DialogPrimitive.Trigger
         data-slot="dialog-trigger"
         {...props}
      />
   )
}

function DialogPortal({ ...props }) {
   return (
      <DialogPrimitive.Portal
         data-slot="dialog-portal"
         {...props}
      />
   )
}

function DialogClose({ ...props }) {
   return (
      <DialogPrimitive.Close
         data-slot="dialog-close"
         {...props}
      />
   )
}

function DialogOverlay({ className, ...props }) {
   return (
      <DialogPrimitive.Overlay
         data-slot="dialog-overlay"
         className={cn(
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/70 backdrop-blur-[3px]",
            className
         )}
         {...props}
      />
   )
}

function DialogContent({
   className,
   children,
   showCloseButton = true,
   ...props
}) {
   return (
      <DialogPortal data-slot="dialog-portal">
         <DialogOverlay />

         <DialogPrimitive.Content
            data-slot="dialog-content"
            className={cn(
               "bg-background border-border-light dark:border-border-dark data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg box-border!",
               className
            )}
            {...props}
         >
            {children}

            {showCloseButton && (
               <DialogPrimitive.Close
                  data-slot="dialog-close"
                  className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
               >
                  <XIcon />
                  <span className="sr-only">Close</span>
               </DialogPrimitive.Close>
            )}
         </DialogPrimitive.Content>
      </DialogPortal>
   )
}

function DialogHeader({ className, ...props }) {
   return (
      <div
         data-slot="dialog-header"
         className={cn(
            "flex flex-col gap-2 text-center sm:text-left",
            className
         )}
         {...props}
      />
   )
}

function DialogFooter({ className, ...props }) {
   return (
      <div
         data-slot="dialog-footer"
         className={cn(
            "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
            className
         )}
         {...props}
      />
   )
}

function DialogTitle({ className, ...props }) {
   return (
      <DialogPrimitive.Title
         data-slot="dialog-title"
         className={cn(
            "text-lg leading-none font-semibold",
            className
         )}
         {...props}
      />
   )
}

function DialogDescription({ className, ...props }) {
   return (
      <DialogPrimitive.Description
         data-slot="dialog-description"
         className={cn(
            "text-muted-foreground text-sm",
            className
         )}
         {...props}
      />
   )
}

export {
   Dialog,
   DialogClose,
   DialogContent,
   DialogDescription,
   DialogFooter,
   DialogHeader,
   DialogOverlay,
   DialogPortal,
   DialogTitle,
   DialogTrigger,
}
